import {
  isAccountId,
  isAreaId,
  isDefined,
  isAttachmentId,
  isCalendarEventId,
  isCompanyId,
  isCompliancePolicyId,
  isComplianceViolationId,
  isConversationId,
  isGameId,
  isStrictFeatureId,
  isProgramId,
  isRecordingId,
  isRoleId,
  isTeamId,
  isUserId,
  isValidEmail,
  isWorkflowId,
} from '@terros/common'
import { type SearchResult } from './useRecentResults'

export type DetectedIdResult = {
  id: string
  type: string
  url: string
}

/**
 * Detects if the search query is a valid entity ID and returns the navigation URL.
 * Returns undefined if the query is not a recognized ID format.
 */
export const detectIdAndGetUrl = (query: string, canViewFeatures = false): DetectedIdResult | undefined => {
  const trimmed = query.trim()
  if (!trimmed) return

  if (canViewFeatures && isStrictFeatureId(trimmed)) {
    return { id: trimmed, type: 'Feature', url: `/feature/${trimmed}` }
  }
  if (isUserId(trimmed)) {
    return { id: trimmed, type: 'User', url: `/user/${trimmed}` }
  }
  if (isTeamId(trimmed)) {
    return { id: trimmed, type: 'Team', url: `/team/${trimmed}` }
  }
  if (isCompanyId(trimmed)) {
    return { id: trimmed, type: 'Company', url: `/companies/${trimmed}` }
  }
  if (isAccountId(trimmed)) {
    return { id: trimmed, type: 'Account', url: `/account/${trimmed}` }
  }
  if (isAreaId(trimmed)) {
    return { id: trimmed, type: 'Area', url: `/areaManagement?area=${encodeURIComponent(trimmed)}` }
  }
  if (isWorkflowId(trimmed)) {
    return { id: trimmed, type: 'Workflow', url: `/settings/workflow/${trimmed}` }
  }
  if (isRoleId(trimmed)) {
    return { id: trimmed, type: 'Role', url: `/settings/role/${trimmed}` }
  }
  if (isAttachmentId(trimmed)) {
    return { id: trimmed, type: 'Attachment', url: `/attachment/${trimmed}` }
  }
  if (isProgramId(trimmed)) {
    return { id: trimmed, type: 'Program', url: `/program/${trimmed}` }
  }
  if (isGameId(trimmed)) {
    return { id: trimmed, type: 'Competition', url: `/competition/${trimmed}` }
  }
  if (isRecordingId(trimmed)) {
    return { id: trimmed, type: 'Recording', url: `/recordings/${trimmed}` }
  }
  if (isCalendarEventId(trimmed)) {
    return { id: trimmed, type: 'Calendar', url: `/calendar?eventId=${trimmed}` }
  }
  if (isConversationId(trimmed)) {
    return { id: trimmed, type: 'Conversation', url: `/conversations/${trimmed}` }
  }
  if (isCompliancePolicyId(trimmed)) {
    return { id: trimmed, type: 'Policy', url: `/compliance/policy/${trimmed}` }
  }
  if (isComplianceViolationId(trimmed)) {
    return { id: trimmed, type: 'Violation', url: `/compliance/violation/${trimmed}` }
  }
  if (isValidEmail(trimmed)) {
    const email = trimmed.toLowerCase()
    return { id: email, type: 'User', url: `/user/${email}` }
  }
}

export const getNavigationUrl = (item: SearchResult): string | undefined => {
  switch (item.type) {
    case 'Destination':
      return item.path
    case 'Account':
      return `/account/${item.accountId}`
    case 'User':
      return `/user/${item.userId}`
    case 'Team':
      return `/team/${item.teamId}`
    case 'Company':
      return `/companies/${item.companyId}`
    case 'Calendar':
      return `/calendar?eventId=${item.eventId}`
    default:
      return
  }
}

export const isSameSearchResult = (a: SearchResult, b: SearchResult): boolean => {
  const url = getNavigationUrl(a)
  return a.type === b.type && isDefined(url) && url === getNavigationUrl(b)
}
